import { useEffect, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import {
  CUSTOMER_TYPES_PATH,
  customerTypePath,
  formatPercent,
  inheritedTypePrice,
  isHiddenByType,
  parseTypePriceInput,
  type CustomerTypeDetail,
  type CustomerTypeSummary,
} from "@/lib/customerTypes";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Settings → Master Menu: one price column per customer type on own prices (price mode
// 'own'; a type on base or percent prices has no column) beside the base price
// (BRIEF-customer-types-zones-picksheet.md §2a "Products page"). A blank cell inherits
// — the price it inherits shows greyed as its placeholder. Type prices are staged and
// saved together (Pitt reprices weekly); the base price keeps saving as the cell is
// left, as it always has. With no customer types none of this renders.

export interface TypePriceColumn {
  id: number;
  name: string;
  priceAdjustPercent: number | null;
  /** Explicit prices, by product id. */
  prices: ReadonlyMap<number, string>;
  /** The type's rules; null until its detail loads. */
  detail: CustomerTypeDetail | null;
  loaded: boolean;
  /** Its prices failed to load: the column stays read-only and says so. */
  failed: boolean;
}

interface GridProduct {
  id: number;
  categoryId: number | null;
  unit?: string | null;
  price?: string | null;
}

const cellKey = (typeId: number, productId: number) => `${typeId}:${productId}`;
const typeIdOf = (key: string) => Number(key.split(":")[0]);
const NO_TYPES: CustomerTypeSummary[] = [];

// Staged prices survive in-app navigation and reloads (wouter has no navigation guard):
// kept per org in this tab's sessionStorage until saved or discarded.
function readDraft(storageKey: string): Map<string, string> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    const entries = raw ? JSON.parse(raw) : [];
    return new Map(Array.isArray(entries) ? entries.filter((e) => Array.isArray(e) && typeof e[0] === "string" && typeof e[1] === "string") : []);
  } catch {
    return new Map();
  }
}
function writeDraft(storageKey: string, staged: Map<string, string>) {
  try {
    if (staged.size === 0) sessionStorage.removeItem(storageKey);
    else sessionStorage.setItem(storageKey, JSON.stringify(Array.from(staged)));
  } catch {
    // Storage unavailable (private mode, quota): the draft lives only in memory.
  }
}

/** Width of one price cell ("$" + input), shared by the cells and the column header. */
export const PRICE_CELL_CLASS = "flex items-center justify-end flex-shrink-0 w-[4.5rem] md:w-20";

export function useTypePriceGrid() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const listKey = [getApiPath(CUSTOMER_TYPES_PATH)];
  const { data: typeList } = useQuery<CustomerTypeSummary[]>({ queryKey: listKey });
  // Only a type on its own prices gets a column: in the other modes its stored prices are
  // unused, so editing them here would change nothing a customer pays.
  const types = (typeList ?? NO_TYPES).filter((type) => type.priceMode === "own");
  const details = useQueries({
    queries: types.map((type) => ({ queryKey: [getApiPath(customerTypePath(type.id))] })),
  });

  const columns: TypePriceColumn[] = types.map((type, i) => {
    const detail = details[i]?.data as CustomerTypeDetail | undefined;
    return {
      id: type.id,
      name: type.name,
      // Own-price mode ignores the percent: a blank cell is the base price.
      priceAdjustPercent: null,
      prices: new Map((detail?.prices ?? []).map((p) => [p.productId, p.price])),
      detail: detail ?? null,
      loaded: detail !== undefined,
      failed: detail === undefined && details[i]?.isError === true,
    };
  });

  // Cells typed but not saved: raw text by `${typeId}:${productId}`.
  const draftKey = `freshorder:type-price-draft:${listKey[0]}`;
  const [staged, setStaged] = useState<Map<string, string>>(() => readDraft(draftKey));
  const [saving, setSaving] = useState(false);

  useEffect(() => writeDraft(draftKey, staged), [draftKey, staged]);

  // A type deleted elsewhere, or taken off own prices, takes its staged cells with it (only
  // once the list has loaded, or a restored draft would be dropped while it loads).
  useEffect(() => {
    if (!typeList) return;
    const ids = new Set(typeList.filter((t) => t.priceMode === "own").map((t) => t.id));
    setStaged((prev) => {
      const next = new Map(Array.from(prev).filter(([key]) => ids.has(typeIdOf(key))));
      return next.size === prev.size ? prev : next;
    });
  }, [typeList]);

  const savedText = (column: TypePriceColumn, productId: number) => column.prices.get(productId) ?? "";

  const cellValue = (column: TypePriceColumn, productId: number) =>
    staged.get(cellKey(column.id, productId)) ?? savedText(column, productId);

  const setCell = (column: TypePriceColumn, productId: number, raw: string) => {
    const key = cellKey(column.id, productId);
    setStaged((prev) => {
      const next = new Map(prev);
      const parsed = parseTypePriceInput(raw);
      const saved = savedText(column, productId);
      // Typed back to what is stored: nothing to save.
      if (parsed.ok && (parsed.price ?? "") === saved) next.delete(key);
      else next.set(key, raw);
      return next;
    });
  };

  const revertCell = (column: TypePriceColumn, productId: number) => {
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(cellKey(column.id, productId));
      return next;
    });
  };

  const isInvalid = (column: TypePriceColumn, productId: number) => {
    const raw = staged.get(cellKey(column.id, productId));
    return raw !== undefined && !parseTypePriceInput(raw).ok;
  };
  const isStaged = (column: TypePriceColumn, productId: number) => staged.has(cellKey(column.id, productId));

  const invalidCount = Array.from(staged.values()).filter((raw) => !parseTypePriceInput(raw).ok).length;

  const discard = () => setStaged(new Map());

  const save = async () => {
    if (invalidCount > 0 || staged.size === 0) return;
    // What is sent; an edit typed while the request is in flight stays staged.
    const sent = new Map(staged);
    const byType = new Map<number, { productId: number; price: string | null }[]>();
    for (const [key, raw] of Array.from(sent)) {
      const [typeId, productId] = key.split(":").map(Number);
      const parsed = parseTypePriceInput(raw);
      if (!parsed.ok) continue;
      const list = byType.get(typeId) ?? [];
      list.push({ productId, price: parsed.price });
      byType.set(typeId, list);
    }
    setSaving(true);
    const batches = Array.from(byType);
    const results = await Promise.allSettled(
      batches.map(([typeId, prices]) => apiRequest("PUT", `${customerTypePath(typeId)}/prices`, { prices })),
    );
    const failed: string[] = [];
    const savedTypes = new Set<number>();
    let saved = 0;
    results.forEach((result, i) => {
      const [typeId, prices] = batches[i];
      if (result.status === "fulfilled") {
        savedTypes.add(typeId);
        saved += prices.length;
        queryClient.invalidateQueries({ queryKey: [getApiPath(customerTypePath(typeId))] });
      } else {
        const name = types.find((t) => t.id === typeId)?.name ?? "a customer type";
        failed.push(`${name}: ${parseApiError(result.reason).message ?? "not saved"}`);
      }
    });
    // Saved cells leave the staging area unless retyped since; a failed type keeps its edits.
    setStaged((prev) => new Map(Array.from(prev).filter(([key, raw]) => !(savedTypes.has(typeIdOf(key)) && sent.get(key) === raw))));
    queryClient.invalidateQueries({ queryKey: listKey });
    setSaving(false);
    if (failed.length > 0) {
      toast({ title: "Some prices weren't saved", description: failed.join(" · "), variant: "destructive" });
    } else {
      toast({ title: `${saved} ${saved === 1 ? "price" : "prices"} saved`, description: "Customers see them the next time their menu loads." });
    }
  };

  // Leaving the page with staged prices would lose them.
  useEffect(() => {
    if (staged.size === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [staged.size]);

  return {
    columns,
    active: columns.length > 0,
    cellValue,
    setCell,
    revertCell,
    isInvalid,
    isStaged,
    stagedCount: staged.size,
    invalidCount,
    saving,
    save,
    discard,
  };
}

export type TypePriceGrid = ReturnType<typeof useTypePriceGrid>;

/**
 * Spreadsheet keys for the price cells: ↓ / Enter go down the column, ↑ / Shift+Enter
 * up, ← / → move across the row at the edge of the text. Returns true when it moved.
 */
export function movePriceFocus(e: React.KeyboardEvent<HTMLInputElement>): boolean {
  const input = e.currentTarget;
  const col = input.dataset.priceCol;
  const row = input.dataset.priceRow;
  if (!col || !row) return false;
  let target: HTMLInputElement | undefined;
  if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
    const cells = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-price-col="${col}"]`));
    const down = e.key === "ArrowDown" || (e.key === "Enter" && !e.shiftKey);
    target = cells[cells.indexOf(input) + (down ? 1 : -1)];
  } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
    const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
    if ((e.key === "ArrowRight" && !atEnd) || (e.key === "ArrowLeft" && !atStart)) return false;
    const cells = Array.from(document.querySelectorAll<HTMLInputElement>(`input[data-price-row="${row}"]`));
    target = cells[cells.indexOf(input) + (e.key === "ArrowRight" ? 1 : -1)];
  } else {
    return false;
  }
  if (!target) {
    if (e.key === "Enter") input.blur();
    return e.key === "Enter";
  }
  e.preventDefault();
  target.focus();
  target.select();
  return true;
}

/** Column names above a category's price cells. */
export function TypePriceHeader({ columns }: { columns: TypePriceColumn[] }) {
  return (
    <div className="flex items-center justify-end gap-2 px-2 md:px-4 pt-2 pb-1 text-xs font-medium text-gray-500" data-testid="type-price-header">
      <span className={PRICE_CELL_CLASS}>Price</span>
      {columns.map((column) => (
        <span
          key={column.id}
          className={`${PRICE_CELL_CLASS} truncate ${column.failed ? "text-red-600" : ""}`}
          title={column.failed ? `${column.name}: prices couldn't load — reload the page` : column.name}
        >
          <span className="truncate">{column.failed ? `${column.name} ⚠` : column.name}</span>
        </span>
      ))}
    </div>
  );
}

/** One customer type's price for one product. Blank shows the inherited price greyed. */
export function TypePriceCell({ grid, column, product }: { grid: TypePriceGrid; column: TypePriceColumn; product: GridProduct }) {
  const inherited = inheritedTypePrice(product.price, column.priceAdjustPercent);
  const invalid = grid.isInvalid(column, product.id);
  const staged = grid.isStaged(column, product.id);
  // A product the type hides: its customers never see it, so say so instead of a price.
  const hidden = column.detail !== null && isHiddenByType(product, column.detail);
  const hint = hidden
    ? `${column.name} customers don't see this product`
    : inherited
      ? `Blank = ${inherited}${column.priceAdjustPercent != null ? ` (base ${formatPercent(column.priceAdjustPercent)})` : " (the base price)"}`
      : "Blank = no price, as the base";
  return (
    <div className={PRICE_CELL_CLASS}>
      <span className="text-gray-500 text-sm mr-1">$</span>
      <Input
        type="text"
        inputMode="decimal"
        value={grid.cellValue(column, product.id)}
        disabled={!column.loaded}
        onChange={(e) => grid.setCell(column, product.id, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            grid.revertCell(column, product.id);
            return;
          }
          movePriceFocus(e);
        }}
        placeholder={hidden ? "hidden" : inherited ?? "—"}
        title={invalid ? parseInvalidMessage(grid.cellValue(column, product.id)) : hint}
        aria-label={`${column.name} price`}
        aria-invalid={invalid || undefined}
        data-price-col={`type-${column.id}`}
        data-price-row={product.id}
        data-testid={`input-type-price-${column.id}-${product.id}`}
        className={`price-input w-14 md:w-16 h-7 text-sm text-right px-1 placeholder:text-gray-400 ${
          invalid ? "border-red-400 bg-red-50" : staged ? "border-amber-300 bg-amber-50" : ""
        }`}
      />
    </div>
  );
}

function parseInvalidMessage(raw: string): string {
  const parsed = parseTypePriceInput(raw);
  return parsed.ok ? "" : parsed.message;
}

/** Save / discard the staged type prices. Renders nothing when nothing is staged. */
export function TypePriceSaveBar({ grid }: { grid: TypePriceGrid }) {
  if (grid.stagedCount === 0) return null;
  return (
    <div
      className="sticky bottom-0 z-30 flex flex-col gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between md:rounded-b-lg"
      data-testid="type-price-save-bar"
    >
      <p className="text-sm text-amber-900">
        {grid.stagedCount} customer-type {grid.stagedCount === 1 ? "price" : "prices"} not saved
        {grid.invalidCount > 0 && <span className="text-red-700"> · {grid.invalidCount} to fix (red)</span>}
        <span className="hidden md:inline text-amber-700"> · ↑ ↓ Enter move down a column, Esc undoes a cell</span>
      </p>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={grid.saving} onClick={grid.discard} data-testid="button-discard-type-prices">
          Discard
        </Button>
        <Button
          type="button"
          size="sm"
          className="bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green"
          disabled={grid.saving || grid.invalidCount > 0}
          onClick={grid.save}
          data-testid="button-save-type-prices"
        >
          {grid.saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save prices
        </Button>
      </div>
    </div>
  );
}
